// Cloudflare Turnstile widget loader + React hook.
// Sitekey is public-by-design (it's the client-side widget identifier
// and gets bundled into the SPA anyway), so it lives in source rather
// than as a build-time env var. The matching SECRET is a Worker secret
// (TURNSTILE_INTAKE_SECRET, see apps/agent/wrangler.toml).
//
// Widget config in CF: name "leap21-intake-vesper5", mode managed,
// domains [apply.enrollment.leap21.ai, leap21-enrollment-intake.pages.dev].

import { useEffect, useId, useRef, useState } from "react";
import { useTheme } from "./theme-context-value";

export const TURNSTILE_SITEKEY = "0x4AAAAAADNPOZWZoOdqe6aZ";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

interface TurnstileGlobal {
  render: (
    container: string | HTMLElement,
    opts: {
      sitekey: string;
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      "timeout-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
      size?: "normal" | "flexible" | "compact";
      action?: string;
    }
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window { turnstile?: TurnstileGlobal }
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      if (window.turnstile) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      // Reset scriptPromise on error so a future useTurnstile() call
      // can retry rather than returning the rejected promise forever
      // (e.g. after a transient network drop and manual page reload).
      existing.addEventListener("error", () => { scriptPromise = null; reject(new Error("turnstile_script_error")); }, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", () => { scriptPromise = null; reject(new Error("turnstile_script_error")); }, { once: true });
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface UseTurnstileResult {
  /** Stable id to attach to the container div. */
  containerId: string;
  /** Solved token, or null until the user passes the challenge. */
  token: string | null;
  /** True until the script + widget have been initialized. */
  loading: boolean;
  /** True when the widget reported an unrecoverable error or expired. */
  error: string | null;
  /** Reset the widget — clears token and prompts the user again. */
  reset: () => void;
}

/**
 * React hook that mounts a Turnstile widget into a container with the
 * returned id. Token is cleared on expiry or error so the parent re-disables
 * the submit button automatically.
 */
export function useTurnstile(): UseTurnstileResult {
  const { resolved, resolvedVersion } = useTheme();
  const containerId = useId();
  const widgetIdRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const [challenge, setChallenge] = useState<{ token: string | null; resolved: "light" | "dark"; generation: number }>({ token: null, resolved, generation: resolvedVersion });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const generation = ++generationRef.current;
    loadScript()
      .then(() => {
        if (cancelled || generation !== generationRef.current || !window.turnstile) return;
        const id = window.turnstile.render(`#${CSS.escape(containerId)}`, {
          sitekey: TURNSTILE_SITEKEY,
          theme: resolved,
          action: "intake-start",
          callback: (t) => { if (generation === generationRef.current) { setChallenge({ token: t, resolved, generation: resolvedVersion }); setError(null); } },
          "expired-callback": () => { if (generation === generationRef.current) { setChallenge({ token: null, resolved, generation: resolvedVersion }); setError("Security check expired — please verify again."); } },
          "error-callback": () => { if (generation === generationRef.current) { setChallenge({ token: null, resolved, generation: resolvedVersion }); setError("Security check failed — please try again."); } },
          "timeout-callback": () => { if (generation === generationRef.current) { setChallenge({ token: null, resolved, generation: resolvedVersion }); setError("Security check timed out — please verify again."); } },
        });
        widgetIdRef.current = id;
        setChallenge({ token: null, resolved, generation: resolvedVersion });
        setLoading(false);
      })
      .catch(() => {
        if (cancelled || generation !== generationRef.current) return;
        setChallenge({ token: null, resolved, generation: resolvedVersion });
        setError("Could not load security check. Disable any extensions blocking Cloudflare and reload.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
      generationRef.current += 1;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Race with React StrictMode double-mount; legitimate noop in
          // production. Surface in dev so an actual API change shows up.
          if (import.meta.env.DEV) console.warn("[turnstile] remove failed", e);
        }
        widgetIdRef.current = null;
      }
    };
  }, [containerId, resolved, resolvedVersion]);

  function reset() {
    setChallenge({ token: null, resolved, generation: resolvedVersion });
    setError(null);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch (e) {
        if (import.meta.env.DEV) console.warn("[turnstile] reset failed", e);
      }
    }
  }

  const currentChallenge = challenge.generation === resolvedVersion && challenge.resolved === resolved;
  const token = currentChallenge ? challenge.token : null;
  return { containerId, token, loading: loading || !currentChallenge, error: currentChallenge ? error : null, reset };
}
